import { theme } from 'theme';
import { twMerge } from 'tailwind-merge';
import { createPortal } from 'react-dom';
import { useDebounceValue } from 'usehooks-ts';
import { Session, Branding } from '@toolpad/core';
import { Route, Routes, useLocation } from 'react-router';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import {
  lazy,
  useMemo,
  useState,
  Suspense,
  useEffect,
  useCallback,
} from 'react';
import {
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { NotFound } from 'pages';
import { getErrorMessage } from 'utils/error';
import { Role, ADMIN_LIKE_ROLES } from 'types';
import { getBrowserState } from 'utils/support';
import { getBrandingLogo } from 'utils/branding';
import { ProtectedRoute } from 'components/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { BrandingLogo } from 'components/branding-logo';
import { Config, AppRoutes, getNavigation } from 'config';
import { ErrorBoundary } from 'components/error-boundary';
import { LIBRARY_TENANT_STORAGE_KEY } from 'utils/storage';
import { useAlert, useSelectedProjectId, useLocalStorageListener } from 'hooks';
import {
  Login,
  Verify,
  SsoCallback,
  SelectVerify,
  ForgotPassword,
  SsoIdpStartLogin,
} from 'pages/auth';
import {
  logout,
  setAuthPath,
  setAuthError,
  selectAuthUser,
  selectAuthError,
  selectIsLoggedIn,
} from 'store/slices/auth';

import { useLoggerHook } from './useLoggerHook';
import { Component, ComponentWithOptions } from './types';

const AppPrivate = lazy(() => import('./app-private'));

function isComponentWithOptions(
  value: Component | ComponentWithOptions
): value is ComponentWithOptions {
  return (value as ComponentWithOptions).allowedForLoggedIn !== undefined;
}

const mappedRoutes = {
  [AppRoutes.Auth.Login]: Login,
  [AppRoutes.Auth.Verify]: Verify,
  [AppRoutes.Auth.SsoCallback]: SsoCallback,
  [AppRoutes.Auth.SelectVerify]: SelectVerify,
  [AppRoutes.Auth.ForgotPassword]: ForgotPassword,
  [AppRoutes.Auth.SsoIdpStartLogin]: SsoIdpStartLogin,
  // [AppRoutes.Auth.ResetPassword]: {
  //   allowedForLoggedIn: true,
  //   Component: ResetPassword,
  // },
};

const homeRoute = AppRoutes.Home;
const authRoute = AppRoutes.Auth.Login;
const savePathOnAuthRedirect = true;

const App = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [browserState] = useState(getBrowserState());

  const { showErrorAlert } = useAlert();
  const dispatch = useAppDispatch();

  useLoggerHook();

  const user = useAppSelector(selectAuthUser);
  const { selectedProjectId } = useSelectedProjectId();
  const { currentValue: libraryTenantId } = useLocalStorageListener(
    LIBRARY_TENANT_STORAGE_KEY
  );

  const libraryEnabled =
    user?.role && ADMIN_LIKE_ROLES.includes(user.role)
      ? true
      : user?.role === Role.TENANT_ADMIN
        ? user?.tenant?.libraryEnabled && user?.isLibraryAccessEnabled
        : user?.role === Role.CONTENT_MANAGER ||
            user?.role === Role.LIBRARY_ADMIN
          ? user?.tenant?.libraryEnabled
          : false;

  const authError = useAppSelector(selectAuthError);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [errorDebounce] = useDebounceValue(error, 1000);
  const location = useLocation();

  const logOut = useCallback(async () => {
    await dispatch(logout({ byUser: true }));
  }, [dispatch]);

  const { session, branding, navigation } = useMemo(() => {
    const session: Session | null = user
      ? {
          user: {
            image: undefined,
            email: user.email,
            name: user.fullName,
          },
        }
      : null;

    const brandingName = Config.brandingName;
    const isLhdMivaxAdmin = user?.role === Role.LHD_MIVAX_ADMIN;
    const brandingProject = isLhdMivaxAdmin ? user.projects?.[0] : null;
    const projectLogo = brandingProject?.logo;

    const branding: Branding = {
      title: user && !isLhdMivaxAdmin ? user?.tenant?.name || brandingName : '',
      logo: user && (
        <BrandingLogo
          alt={user?.tenant?.name || brandingName}
          src={projectLogo || user?.tenant?.logo || getBrandingLogo()}
          className={twMerge((projectLogo || user?.tenant?.logo) && 'max-h-6!')}
          darkThemeSrc={
            projectLogo || user?.tenant?.siteLogo || getBrandingLogo()
          }
        />
      ),
    };

    return {
      session,
      branding,
      navigation: getNavigation(user, {
        libraryEnabled,
        selectedProjectId,
        libraryTenantId: libraryTenantId ?? undefined,
      }),
    };
  }, [libraryEnabled, libraryTenantId, selectedProjectId, user]);

  const authentication = useMemo(
    () => ({
      signOut: logOut,
      signIn: () => {},
    }),
    [logOut]
  );

  useEffect(() => {
    if (authError) {
      setError(getErrorMessage(authError));
      dispatch(setAuthError(undefined));
    }
  }, [authError]);

  const savePath = useCallback(() => {
    dispatch(setAuthPath({ path: location.pathname + location.search }));
  }, [dispatch, location.pathname, location.search]);

  useEffect(() => {
    if (errorDebounce) {
      showErrorAlert(errorDebounce);
      setError(undefined);
    }
  }, [errorDebounce]);

  const isWafEnabled = Config.waf.enabled;

  const captchaElement = isWafEnabled
    ? createPortal(
        <div
          id="modalOverlay"
          className="fixed inset-0 z-[9999] flex hidden items-center justify-center bg-black/60"
        >
          <div
            id="captchaForm"
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-4"
          />
        </div>,
        document.body
      )
    : undefined;

  return (
    <>
      <ReactRouterAppProvider
        theme={theme}
        session={session}
        branding={branding}
        navigation={navigation}
        authentication={authentication}
      >
        <ErrorBoundary>
          <Routes>
            <Route
              path={AppRoutes.NotFound}
              element={
                <Suspense>
                  <NotFound homePath={homeRoute} />
                </Suspense>
              }
            />
            {Object.keys(mappedRoutes).map((path) => {
              const route = mappedRoutes[path];
              const hasOptions = isComponentWithOptions(route);
              const Page = hasOptions ? route.Component : route;
              return (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute
                      redirect={homeRoute}
                      allowed={
                        hasOptions ? route.allowedForLoggedIn : !isLoggedIn
                      }
                    >
                      <Suspense>
                        <Page />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
              );
            })}
            <Route
              path="*"
              element={
                <ProtectedRoute
                  allowed={isLoggedIn}
                  redirect={authRoute}
                  onWillRedirect={savePathOnAuthRedirect ? savePath : undefined}
                >
                  <Suspense>
                    <AppPrivate />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          </Routes>
        </ErrorBoundary>
      </ReactRouterAppProvider>

      {browserState.outdated && (
        <Dialog open onClose={() => {}}>
          <DialogTitle>Update your browser</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              In order to ensure the correct operation of the platform, update
              your browser to the latest version.
            </Typography>
            <Typography gutterBottom>
              To find out how to update your browser, please open the link below
              and follow the instructions.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                window.open(
                  'https://www.wikihow.com/Update-Your-Browser',
                  '_blank'
                )
              }
            >
              How to update web browser
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {captchaElement}
    </>
  );
};

export default App;
